import { ProgramFixedContentRow, ProgramFixedContentRowUpsert } from '../types/domain';
import { supabase } from '../supabaseClient';
import {
  clearProgramFixedContentRowsCache,
  loadProgramFixedContentRowsCache,
  mergeProgramFixedContentRowsCacheRows,
  saveProgramFixedContentRowsCache,
} from '../utils/programFixedContentRowsCache';
import { setProgramFixedRowsQueryData } from '../query/cacheUpdates';
import { notifySchoolProgramMutation } from '../utils/schoolDataCacheVersion';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  copyProgramContentSlidePagesBetweenOwners,
  forkPresentationRefAfterScopedOrFixedRowDuplicate,
  parseSlideCopyFromFixedRowId,
} from './programContentSlideService';
import { forkWorksheetRefAfterScopedOrFixedRowDuplicate } from './programContentWorksheetService';
import { normalizeFixedContentRowForStorage } from '../utils/programContentNullNormalize';
import {
  buildLearningContentScopeVersion,
  learningContentScopeVersionFromHead,
} from '../utils/learningContentCacheVersion';
import {
  applyProgramContentRowsDiff,
  planProgramContentRowsDiff,
  type ProgramContentDiffChange,
} from '../utils/programContentRowsDiff';
import {
  forkSharedProgramContentRefs,
  preserveRemoteProgramContentAttachmentRefs,
  isMissingProgramContentDiffRpcError,
  isStaleBaselineProgramContentDiffRpcError,
} from './programContentRowsDiffSave';

/** `deferSchoolVersionNotify`: true면 `notifySchoolProgramMutation` 생략(통합 저장 배치 끝에서 한 번 호출용) */
export type ProgramMutationNotifyOptions = {
  deferSchoolVersionNotify?: boolean;
};

const PROGRAM_FIXED_CONTENT_SELECT = [
  'id',
  'workspace_id',
  'grade_id',
  'academic_year',
  'term_number',
  'order_index',
  'source_annual_cell_key',
  'event_name',
  'timetable_text',
  'unit_text',
  'learning_text',
  'learning_note',
  'presentation_ref_id',
  'worksheet_ref_id',
  'page_text',
  'assistant_page_text',
  'current_period_text',
  'total_period_text',
  'material_text',
  'record_cross_curricular_text',
  'record_memo_text',
  'record_evaluation_text',
  'record_class_text',
  'record_member_group_id',
  'bg_color_by_field',
  'created_at',
  'updated_at',
].join(', ');

const PROGRAM_FIXED_CONTENT_PAGE_SIZE = 1000;

function ensureFixedLearningNoteNotNull<T extends { learning_note?: string | null }>(row: T): T {
  return { ...row, learning_note: row.learning_note ?? '' };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function normalizeUuidOrNull(value: unknown): string | null {
  const s = String(value ?? '').trim();
  return s && UUID_RE.test(s) ? s : null;
}

function sanitizeFixedRowForDb<T extends Record<string, unknown>>(row: T): T {
  return {
    ...row,
    timetable_text: (row.timetable_text as string | null | undefined) ?? '',
    unit_text: (row.unit_text as string | null | undefined) ?? '',
    learning_text: (row.learning_text as string | null | undefined) ?? '',
    learning_note: (row.learning_note as string | null | undefined) ?? '',
    page_text: (row.page_text as string | null | undefined) ?? '',
    assistant_page_text: (row.assistant_page_text as string | null | undefined) ?? '',
    current_period_text: (row.current_period_text as string | null | undefined) ?? '',
    total_period_text: (row.total_period_text as string | null | undefined) ?? '',
    material_text: (row.material_text as string | null | undefined) ?? '',
    record_cross_curricular_text: (row.record_cross_curricular_text as string | null | undefined) ?? '',
    record_memo_text: (row.record_memo_text as string | null | undefined) ?? '',
    record_evaluation_text: (row.record_evaluation_text as string | null | undefined) ?? '',
    record_class_text: (row.record_class_text as string | null | undefined) ?? '',
    presentation_ref_id: normalizeUuidOrNull(row.presentation_ref_id),
    worksheet_ref_id: normalizeUuidOrNull(row.worksheet_ref_id),
    record_member_group_id: normalizeUuidOrNull(row.record_member_group_id),
    bg_color_by_field: (row.bg_color_by_field as Record<string, string> | null | undefined) ?? {},
  } as T;
}

function formatSupabaseErrorMessage(error: unknown, context: string): string {
  if (!error || typeof error !== 'object') return `${context}: unknown error`;
  const e = error as { message?: string; details?: string; hint?: string; code?: string };
  const bits = [e.message, e.details, e.hint, e.code].map((v) => String(v ?? '').trim()).filter(Boolean);
  return bits.length ? `${context}: ${bits.join(' | ')}` : `${context}: request failed`;
}

function normalizeSourceAnnualKey(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function rowFreshness(row: { updated_at?: string | null; created_at?: string | null }): string {
  return String(row.updated_at ?? row.created_at ?? '');
}

function normalizeFixedRowsForDisplay(rows: ProgramFixedContentRow[]): ProgramFixedContentRow[] {
  if (!rows.length) return rows;
  const byId = new Map<string, ProgramFixedContentRow>();
  const noIdRows: ProgramFixedContentRow[] = [];
  rows.forEach((row) => {
    const id = (row.id ?? '').trim();
    if (!id) {
      noIdRows.push(row);
      return;
    }
    const prev = byId.get(id);
    if (!prev || rowFreshness(row) >= rowFreshness(prev)) byId.set(id, row);
  });

  const bySource = new Map<string, ProgramFixedContentRow>();
  const passthrough: ProgramFixedContentRow[] = [];
  [...noIdRows, ...Array.from(byId.values())].forEach((row) => {
    const sourceKey = normalizeSourceAnnualKey(row.source_annual_cell_key);
    if (!sourceKey) {
      passthrough.push(row);
      return;
    }
    const naturalKey = [
      row.workspace_id,
      row.grade_id,
      row.academic_year,
      row.term_number,
      sourceKey,
    ].join('|');
    const prev = bySource.get(naturalKey);
    if (!prev || rowFreshness(row) >= rowFreshness(prev)) bySource.set(naturalKey, row);
  });
  return [...passthrough, ...Array.from(bySource.values())].sort(
    (a, b) =>
      (a.order_index - b.order_index) ||
      String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''))
  );
}

async function fetchAllProgramFixedContentRows(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): Promise<ProgramFixedContentRow[]> {
  const rows: ProgramFixedContentRow[] = [];
  let offset = 0;
  for (;;) {
    const { data, error } = await supabase
      .from('program_fixed_content_rows')
      .select(PROGRAM_FIXED_CONTENT_SELECT)
      .eq('workspace_id', workspaceId)
      .eq('academic_year', academicYear)
      .eq('term_number', termNumber)
      .eq('grade_id', gradeId)
      .order('order_index', { ascending: true })
      .order('created_at', { ascending: true })
      .range(offset, offset + PROGRAM_FIXED_CONTENT_PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    const batch = (data ?? []) as unknown as ProgramFixedContentRow[];
    rows.push(...batch);
    if (batch.length < PROGRAM_FIXED_CONTENT_PAGE_SIZE) break;
    offset += PROGRAM_FIXED_CONTENT_PAGE_SIZE;
  }
  return rows;
}

/** 고정행 head — `count:max(updated_at)` */
export async function fetchProgramFixedContentRowsHead(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): Promise<string> {
  if (!workspaceId || !gradeId) return '0:';
  const { count, error: countError } = await supabase
    .from('program_fixed_content_rows')
    .select('id', { count: 'exact', head: true })
    .eq('workspace_id', workspaceId)
    .eq('academic_year', academicYear)
    .eq('term_number', termNumber)
    .eq('grade_id', gradeId);
  if (countError) throw new Error(countError.message);
  if (!count) return learningContentScopeVersionFromHead(0, '');
  const { data, error } = await supabase
    .from('program_fixed_content_rows')
    .select('updated_at')
    .eq('workspace_id', workspaceId)
    .eq('academic_year', academicYear)
    .eq('term_number', termNumber)
    .eq('grade_id', gradeId)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) throw new Error(error.message);
  const maxUpdatedAt = (data?.[0] as { updated_at?: string } | undefined)?.updated_at ?? '';
  return learningContentScopeVersionFromHead(count, maxUpdatedAt);
}

export async function getProgramFixedContentRows(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  options?: { forceRefresh?: boolean }
): Promise<ProgramFixedContentRow[]> {
  if (!workspaceId || !gradeId) return [];
  if (!options?.forceRefresh) {
    const cached = loadProgramFixedContentRowsCache(workspaceId, academicYear, termNumber, gradeId);
    if (cached.found && cached.contentVersion) {
      try {
        const headVersion = await fetchProgramFixedContentRowsHead(
          workspaceId,
          academicYear,
          termNumber,
          gradeId
        );
        if (cached.contentVersion === headVersion) {
          recordDevLocalStorageProgramCacheUse('고정행');
          return cached.rows;
        }
      } catch {
        /* head 실패 시 풀 조회 */
      }
    } else if (cached.found) {
      recordDevLocalStorageProgramCacheUse('고정행');
      return cached.rows;
    }
  }
  const rows = normalizeFixedRowsForDisplay(
    await fetchAllProgramFixedContentRows(workspaceId, academicYear, termNumber, gradeId)
  );
  const contentVersion = buildLearningContentScopeVersion(rows);
  saveProgramFixedContentRowsCache(workspaceId, academicYear, termNumber, gradeId, rows, {
    contentVersion,
  });
  return rows;
}

/** 고아 행 판별용 — 텍스트 컬럼 없이 id만 적재 */
export async function listProgramFixedContentRowIds(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): Promise<string[]> {
  if (!workspaceId || !gradeId) return [];
  const ids: string[] = [];
  let offset = 0;
  for (;;) {
    const { data, error } = await supabase
      .from('program_fixed_content_rows')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('academic_year', academicYear)
      .eq('term_number', termNumber)
      .eq('grade_id', gradeId)
      .order('order_index', { ascending: true })
      .order('created_at', { ascending: true })
      .range(offset, offset + PROGRAM_FIXED_CONTENT_PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    const batch = (data ?? []) as Array<{ id: string }>;
    batch.forEach((r) => {
      if (r.id) ids.push(r.id);
    });
    if (batch.length < PROGRAM_FIXED_CONTENT_PAGE_SIZE) break;
    offset += PROGRAM_FIXED_CONTENT_PAGE_SIZE;
  }
  return ids;
}

export async function upsertProgramFixedContentRows(
  rows: ProgramFixedContentRowUpsert[],
  options?: ProgramMutationNotifyOptions
): Promise<ProgramFixedContentRow[]> {
  if (!rows.length) return [];
  const now = new Date().toISOString();
  const naturalKey = (row: {
    workspace_id: string;
    grade_id: string;
    academic_year: number;
    term_number: number;
    source_annual_cell_key?: string | null;
  }) =>
    [
      row.workspace_id,
      row.grade_id,
      row.academic_year,
      row.term_number,
      normalizeSourceAnnualKey(row.source_annual_cell_key),
    ].join('|');
  const rowsWithSourceAnnual = rows.filter((row) => !!normalizeSourceAnnualKey(row.source_annual_cell_key));
  const rowsWithoutSourceAnnual = rows.filter((row) => !normalizeSourceAnnualKey(row.source_annual_cell_key));

  const withId = rowsWithoutSourceAnnual
    .filter((row) => !!row.id?.trim())
    .map((row) => {
      const {
        _slidePageCopyFromFixedRowId: _cf,
        _slidePageCopyFromScopedRowId: _cs,
        _skipFixedTablePersist: _sk,
        ...rest
      } = row;
      const normalized = normalizeFixedContentRowForStorage(rest as ProgramFixedContentRowUpsert);
      return {
        ...ensureFixedLearningNoteNotNull(normalized),
        id: row.id!.trim(),
        updated_at: now,
      };
    });
  const withoutId = rows
    .filter((row) => !normalizeSourceAnnualKey(row.source_annual_cell_key))
    .filter((row) => !row.id?.trim())
    .map(
      ({
        id: _ignoreId,
        _slidePageCopyFromFixedRowId: _cf,
        _slidePageCopyFromScopedRowId: _cs,
        _skipFixedTablePersist: _sk,
        ...rest
      }) => ({
      ...ensureFixedLearningNoteNotNull(normalizeFixedContentRowForStorage(rest as ProgramFixedContentRowUpsert)),
      updated_at: now,
    }));

  const saved: ProgramFixedContentRow[] = [];

  if (withId.length) {
    const safeWithId = withId.map((row) => sanitizeFixedRowForDb(row));
    const { data, error } = await supabase
      .from('program_fixed_content_rows')
      .upsert(safeWithId, { onConflict: 'id' })
      .select(PROGRAM_FIXED_CONTENT_SELECT);
    if (error) {
      console.error('[program-fixed-upsert:withId] failed payload sample', safeWithId[0]);
      throw new Error(formatSupabaseErrorMessage(error, 'program_fixed_content_rows upsert(withId)'));
    }
    saved.push(...(((data ?? []) as unknown) as ProgramFixedContentRow[]));
  }
  if (rowsWithSourceAnnual.length) {
    const slideCopyFromByNaturalKey = new Map<string, string>();
    rowsWithSourceAnnual.forEach((row) => {
      const fromId = parseSlideCopyFromFixedRowId(row);
      if (!fromId) return;
      slideCopyFromByNaturalKey.set(naturalKey(row), fromId);
    });
    const naturalRaw = rowsWithSourceAnnual.map((row) => {
      const {
        id: _ignoreId,
        _slidePageCopyFromFixedRowId: _cf,
        _slidePageCopyFromScopedRowId: _cs,
        _skipFixedTablePersist: _sk,
        ...rest
      } = row;
      const normalized = normalizeFixedContentRowForStorage(rest as ProgramFixedContentRowUpsert);
      return {
        ...ensureFixedLearningNoteNotNull(normalized),
        source_annual_cell_key: normalizeSourceAnnualKey(row.source_annual_cell_key),
        updated_at: now,
      };
    });
    const naturalRows = Array.from(
      naturalRaw
        .reduce((m, row) => m.set(naturalKey(row), row), new Map<string, (typeof naturalRaw)[number]>())
        .values()
    );
    const anchor = naturalRows[0];
    const sourceAnnualKeys = Array.from(
      new Set(naturalRows.map((row) => normalizeSourceAnnualKey(row.source_annual_cell_key)).filter(Boolean))
    );
    const existingByNatural = new Map<string, string>();
    if (anchor && sourceAnnualKeys.length) {
      const { data: existingRows, error: existingError } = await supabase
        .from('program_fixed_content_rows')
        .select('id, workspace_id, grade_id, academic_year, term_number, source_annual_cell_key')
        .eq('workspace_id', anchor.workspace_id)
        .eq('grade_id', anchor.grade_id)
        .eq('academic_year', anchor.academic_year)
        .eq('term_number', anchor.term_number)
        .in('source_annual_cell_key', sourceAnnualKeys)
        .order('updated_at', { ascending: false })
        .order('created_at', { ascending: false });
      if (existingError) throw new Error(existingError.message);
      (existingRows ?? []).forEach((row) => {
        const id = String(row.id ?? '').trim();
        if (!id) return;
        const key = naturalKey({
          workspace_id: String(row.workspace_id ?? ''),
          grade_id: String(row.grade_id ?? ''),
          academic_year: Number(row.academic_year ?? 0),
          term_number: Number(row.term_number ?? 0),
          source_annual_cell_key: String(row.source_annual_cell_key ?? ''),
        });
        if (existingByNatural.has(key)) return;
        existingByNatural.set(
          key,
          id
        );
      });
    }
    const naturalWithId = naturalRows.map((row) => ({
      ...row,
      id: existingByNatural.get(naturalKey(row)),
    }));
    const updates = naturalWithId
      .filter((row) => !!(row.id ?? '').trim())
      .map((row) => sanitizeFixedRowForDb(row));
    const inserts = naturalWithId
      .filter((row) => !(row.id ?? '').trim())
      .map(({ id: _id, ...rest }) => sanitizeFixedRowForDb(rest));
    const upserted: ProgramFixedContentRow[] = [];
    if (updates.length) {
      const { data, error } = await supabase
        .from('program_fixed_content_rows')
        .upsert(updates, { onConflict: 'id' })
        .select(PROGRAM_FIXED_CONTENT_SELECT);
      if (error) {
        console.error('[program-fixed-upsert:updates] failed payload sample', updates[0]);
        throw new Error(formatSupabaseErrorMessage(error, 'program_fixed_content_rows upsert(updates)'));
      }
      upserted.push(...(((data ?? []) as unknown) as ProgramFixedContentRow[]));
    }
    if (inserts.length) {
      const { data, error } = await supabase
        .from('program_fixed_content_rows')
        .insert(inserts)
        .select(PROGRAM_FIXED_CONTENT_SELECT);
      if (error) {
        console.error('[program-fixed-upsert:inserts] failed payload sample', inserts[0]);
        throw new Error(formatSupabaseErrorMessage(error, 'program_fixed_content_rows insert(inserts)'));
      }
      upserted.push(...(((data ?? []) as unknown) as ProgramFixedContentRow[]));
    }
    saved.push(...upserted);
    const sid = upserted[0]?.workspace_id ?? rows[0]?.workspace_id ?? '';
    if (sid) {
      for (const row of upserted) {
        const fromId = slideCopyFromByNaturalKey.get(naturalKey(row));
        const newId = row.id?.trim();
        if (!fromId || !newId || fromId === newId) continue;
        try {
          await copyProgramContentSlidePagesBetweenOwners(sid, { kind: 'fixed_row', id: fromId }, { kind: 'fixed_row', id: newId });
        } catch {
          /* 슬라이드 복제 실패는 본 저장과 분리 */
        }
        await forkPresentationRefAfterScopedOrFixedRowDuplicate({
          workspaceId: sid,
          table: 'program_fixed_content_rows',
          newRowId: newId,
          learningText: row.learning_text,
        });
        await forkWorksheetRefAfterScopedOrFixedRowDuplicate({
          workspaceId: sid,
          table: 'program_fixed_content_rows',
          newRowId: newId,
          learningText: row.learning_text,
        });
      }
    }
  }
  if (withoutId.length) {
    const safeWithoutId = withoutId.map((row) => sanitizeFixedRowForDb(row));
    const withoutIdAugmented = rows.filter((row) => !row.id?.trim());
    const slideCopyFromIds = withoutIdAugmented.map((row) => parseSlideCopyFromFixedRowId(row));
    const { data, error } = await supabase
      .from('program_fixed_content_rows')
      .insert(safeWithoutId)
      .select(PROGRAM_FIXED_CONTENT_SELECT);
    if (error) {
      console.error('[program-fixed-upsert:withoutId] failed payload sample', safeWithoutId[0]);
      throw new Error(formatSupabaseErrorMessage(error, 'program_fixed_content_rows insert(withoutId)'));
    }
    const inserted = ((data ?? []) as unknown) as ProgramFixedContentRow[];
    saved.push(...inserted);
    const sid = inserted[0]?.workspace_id ?? rows[0]?.workspace_id ?? '';
    if (sid) {
      for (let i = 0; i < inserted.length; i++) {
        const fromId = slideCopyFromIds[i];
        const newId = inserted[i]?.id?.trim();
        if (!fromId || !newId || fromId === newId) continue;
        try {
          await copyProgramContentSlidePagesBetweenOwners(sid, { kind: 'fixed_row', id: fromId }, { kind: 'fixed_row', id: newId });
        } catch {
          /* 슬라이드 복제 실패는 본 저장과 분리 */
        }
        await forkPresentationRefAfterScopedOrFixedRowDuplicate({
          workspaceId: sid,
          table: 'program_fixed_content_rows',
          newRowId: newId,
          learningText: inserted[i]?.learning_text,
        });
        await forkWorksheetRefAfterScopedOrFixedRowDuplicate({
          workspaceId: sid,
          table: 'program_fixed_content_rows',
          newRowId: newId,
          learningText: inserted[i]?.learning_text,
        });
      }
    }
  }

  const first = saved[0] ?? rows[0];
  const workspaceId = first?.workspace_id ?? '';
  const ay = first?.academic_year;
  const tn = first?.term_number;
  const gid = first?.grade_id;
  if (workspaceId && gid != null && ay != null && tn != null) {
    const cached = loadProgramFixedContentRowsCache(workspaceId, ay, tn as 1 | 2, gid);
    if (cached.found) {
      const merged = normalizeFixedRowsForDisplay(
        mergeProgramFixedContentRowsCacheRows(cached.rows, saved)
      );
      saveProgramFixedContentRowsCache(workspaceId, ay, tn as 1 | 2, gid, merged);
      setProgramFixedRowsQueryData(workspaceId, ay, tn as 1 | 2, gid, merged);
    } else {
      clearProgramFixedContentRowsCache(workspaceId, ay, tn as 1 | 2, gid);
    }
  }
  if (!options?.deferSchoolVersionNotify && workspaceId) {
    await notifySchoolProgramMutation(workspaceId);
  }
  return saved;
}

const SAVE_FIXED_ROWS_DIFF_RPC = 'save_program_fixed_content_rows_diff';

function buildFixedRowsDiffRpcRow(change: ProgramContentDiffChange<ProgramFixedContentRowUpsert>, forceFull: boolean): Record<string, unknown> {
  const { row, clientKey } = change;
  if (change.positionOnly && !forceFull && row.id) {
    return {
      client_key: clientKey,
      id: row.id,
      position_only: true,
      order_index: row.order_index,
      source_annual_cell_key: row.source_annual_cell_key ?? null,
    };
  }
  const {
    _slidePageCopyFromFixedRowId: _cf,
    _slidePageCopyFromScopedRowId: _cs,
    _skipFixedTablePersist: _sk,
    ...rest
  } = row;
  const normalized = sanitizeFixedRowForDb(
    ensureFixedLearningNoteNotNull(
      normalizeFixedContentRowForStorage(rest as ProgramFixedContentRowUpsert)
    ) as unknown as Record<string, unknown>
  );
  return { ...normalized, id: row.id ?? null, client_key: clientKey };
}

export type SaveProgramFixedContentRowsDiffParams = {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  /** 마지막으로 불러온 서버 목록(삭제 판별 기준 — 여기 없던 서버 행은 지우지 않는다) */
  baselineRows: readonly ProgramFixedContentRow[];
  /** 화면 표 전체 스냅샷 */
  snapshotRows: readonly ProgramFixedContentRowUpsert[];
};

/**
 * 화면 스냅샷 = 저장본. 기준본과의 차이(변경·추가 행, 사라진 행 id)만 DB 함수 한 번으로 반영하고
 * 캐시는 합치지 않고 결과로 교체한다. 저장 후 행 수가 서버와 다르면 풀 조회로 바로잡는다.
 */
export async function saveProgramFixedContentRowsDiff(
  params: SaveProgramFixedContentRowsDiffParams,
  options?: ProgramMutationNotifyOptions
): Promise<ProgramFixedContentRow[]> {
  const { workspaceId, academicYear, termNumber, gradeId, baselineRows } = params;
  if (!workspaceId || !gradeId) return [...baselineRows];
  const plan = planProgramContentRowsDiff(baselineRows, params.snapshotRows);
  if (!plan.changes.length && !plan.deleteIds.length) return [...baselineRows];

  await preserveRemoteProgramContentAttachmentRefs('program_fixed_content_rows', workspaceId, baselineRows, plan);
  await forkSharedProgramContentRefs(workspaceId, baselineRows, plan);

  const callRpc = (forceFull: boolean) =>
    supabase.rpc(SAVE_FIXED_ROWS_DIFF_RPC, {
      p_workspace_id: workspaceId,
      p_grade_id: gradeId,
      p_academic_year: academicYear,
      p_term_number: termNumber,
      p_rows: plan.changes.map((c) => buildFixedRowsDiffRpcRow(c, forceFull)),
      p_delete_ids: plan.deleteIds,
    });

  let { data, error } = await callRpc(false);
  if (error && isStaleBaselineProgramContentDiffRpcError(error)) {
    ({ data, error } = await callRpc(true));
  }

  let nextRows: ProgramFixedContentRow[];
  if (error && isMissingProgramContentDiffRpcError(error)) {
    /** DB 함수 미적용 환경: 삭제 → 변경 행 upsert 순서(슬롯 자연키 재사용이 삭제 행을 되살리지 않게) */
    if (plan.deleteIds.length) {
      await deleteProgramFixedContentRowsByIds(plan.deleteIds, { deferSchoolVersionNotify: true });
    }
    if (plan.changes.length) {
      await upsertProgramFixedContentRows(
        plan.changes.map((c) => c.row),
        { deferSchoolVersionNotify: true }
      );
    }
    nextRows = await getProgramFixedContentRows(workspaceId, academicYear, termNumber, gradeId, {
      forceRefresh: true,
    });
  } else if (error) {
    throw new Error(formatSupabaseErrorMessage(error, SAVE_FIXED_ROWS_DIFF_RPC));
  } else {
    const payload = (data ?? {}) as { rows?: unknown; count?: number; max_updated_at?: string | null };
    const savedRows = (Array.isArray(payload.rows) ? payload.rows : []).map((raw) => {
      const { client_key: _ck, ...row } = raw as Record<string, unknown>;
      return row as unknown as ProgramFixedContentRow;
    });
    const applied = normalizeFixedRowsForDisplay(
      applyProgramContentRowsDiff(baselineRows, savedRows, plan.deleteIds)
    );
    const serverCount = typeof payload.count === 'number' ? payload.count : -1;
    if (serverCount !== applied.length) {
      nextRows = await getProgramFixedContentRows(workspaceId, academicYear, termNumber, gradeId, {
        forceRefresh: true,
      });
    } else {
      nextRows = applied;
      saveProgramFixedContentRowsCache(workspaceId, academicYear, termNumber, gradeId, applied, {
        contentVersion: learningContentScopeVersionFromHead(serverCount, payload.max_updated_at ?? ''),
      });
    }
  }
  setProgramFixedRowsQueryData(workspaceId, academicYear, termNumber, gradeId, nextRows);
  if (!options?.deferSchoolVersionNotify) {
    await notifySchoolProgramMutation(workspaceId);
  }
  return nextRows;
}

export async function deleteProgramFixedContentRowsByIds(
  ids: string[],
  options?: ProgramMutationNotifyOptions
): Promise<void> {
  const targetIds = ids.map((v) => v.trim()).filter(Boolean);
  if (!targetIds.length) return;
  const { data: rowsToDelete } = await supabase
    .from('program_fixed_content_rows')
    .select('id, workspace_id, academic_year, term_number, grade_id')
    .in('id', targetIds);
  const { error } = await supabase.from('program_fixed_content_rows').delete().in('id', targetIds);
  if (error) throw new Error(error.message);
  const deletedRows = ((rowsToDelete ?? []) as Array<{
    id: string;
    workspace_id: string;
    academic_year: number;
    term_number: 1 | 2;
    grade_id: string;
  }>).filter((row) => !!row.id && !!row.workspace_id && !!row.grade_id);
  const grouped = new Map<string, { workspaceId: string; academicYear: number; termNumber: 1 | 2; gradeId: string; ids: Set<string> }>();
  deletedRows.forEach((row) => {
    const key = `${row.workspace_id}|${row.academic_year}|${row.term_number}|${row.grade_id}`;
    const prev = grouped.get(key);
    if (prev) {
      prev.ids.add(row.id);
      return;
    }
    grouped.set(key, {
      workspaceId: row.workspace_id,
      academicYear: row.academic_year,
      termNumber: row.term_number,
      gradeId: row.grade_id,
      ids: new Set([row.id]),
    });
  });
  const workspaceId = deletedRows[0]?.workspace_id ?? '';
  grouped.forEach(({ workspaceId: sid, academicYear, termNumber, gradeId }) => {
    const cached = loadProgramFixedContentRowsCache(sid, academicYear, termNumber, gradeId);
    if (cached.found) {
      const ids = new Set(
        deletedRows
          .filter(
            (row) =>
              row.workspace_id === sid &&
              row.academic_year === academicYear &&
              row.term_number === termNumber &&
              row.grade_id === gradeId
          )
          .map((row) => row.id)
      );
      saveProgramFixedContentRowsCache(
        sid,
        academicYear,
        termNumber,
        gradeId,
        cached.rows.filter((row) => !ids.has(row.id))
      );
    } else {
      clearProgramFixedContentRowsCache(sid, academicYear, termNumber, gradeId);
    }
  });
  if (!options?.deferSchoolVersionNotify && workspaceId) {
    await notifySchoolProgramMutation(workspaceId);
  }
}
